import { type AppSpec, getIzimiMetadataHeaders } from './util';

/* eslint-disable no-console */
type ObjectType = Record<string, any>;

type Options = {
  resAsBlob?: boolean;
  withResponseHeaders?: boolean;
  getRawSuccessResponse?: boolean;
};

type UploadDocumentOptions = {
  signal: AbortSignal;
};

interface Providers {
  getAccessToken?: () => Promise<string | null | undefined>;
  handleErrorResponse: (res: Response | XMLHttpRequest) => void | never;
  getLanguage: () => string;
  logRequests: boolean;
  getAppSpec: () => AppSpec;
}

class FetchService {
  public apiUrl: string | undefined;
  public appSpec: AppSpec;
  private headers: ObjectType;
  private token: string | undefined;
  private mockApiKey: string;

  constructor(private providers: Providers) {
    this.headers = {
      'Content-type': 'application/json',
      Accept: 'application/json',
    };
    this.appSpec = providers.getAppSpec();
  }

  getToken() {
    return this.token ?? this.providers.getAccessToken?.();
  }

  setToken(token: string) {
    this.token = token;
  }

  setApiUrl(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  setMockApiKey(key: string) {
    this.mockApiKey = key;
  }

  get<ReturnType>(url: string, options?: Options, headers?: ObjectType) {
    return this.request<ReturnType>({
      url,
      params: { method: 'GET' },
      headers,
      options,
    });
  }

  post<ReturnType>(url: string, body?: unknown, headers?: ObjectType, options?: Options) {
    return this.request<ReturnType>({
      url,
      params: {
        body: body instanceof FormData || typeof body === 'string' ? body : JSON.stringify(body),
        method: 'POST',
      },
      headers,
      options,
    });
  }

  async uploadFile<ReturnType>(
    url: string,
    formData: FormData,
    onProgress: XMLHttpRequestEventTarget['onprogress'],
    options?: UploadDocumentOptions,
  ) {
    const token = await this.getToken();
    const DONE_STATE = 4;

    return new Promise<ReturnType>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      if (options?.signal && options?.signal.aborted) {
        reject(new Error('client.technical.uploadAborted'));
      }

      function abort() {
        xhr.abort();
      }

      if (options?.signal) {
        options?.signal.addEventListener('abort', abort);

        xhr.onreadystatechange = () => {
          if (xhr.readyState === DONE_STATE) {
            options?.signal.removeEventListener('abort', abort);
          }
        };
      }

      xhr.onabort = () => {
        reject(new Error('client.technical.uploadAborted'));
      };

      xhr.open('POST', this.apiUrl + url);
      xhr.setRequestHeader('Authorization', `bearer ${token}`);

      // Add Izimi metadata headers
      const appSpec = this.providers.getAppSpec();
      const izimiHeaders = getIzimiMetadataHeaders({ appSpec });
      Object.entries(izimiHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.responseType = 'text';
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // the backend firewall might send a 200 OK with a html error page
          // so we need to check if the the response is json and 200 OK
          const text = await xhr.response;
          try {
            const json = JSON.parse(text);
            return resolve(json);
          } catch {
            return reject(this.providers.handleErrorResponse(xhr));
          }
        }
        return reject(this.providers.handleErrorResponse(xhr));
      };
      xhr.onerror = () => reject(new TypeError('Network request failed'));
      xhr.upload.onprogress = onProgress;
      xhr.send(formData);
    });
  }

  put<ReturnType>(url: string, body?: unknown) {
    return this.request<ReturnType>({
      url,
      params: {
        body: JSON.stringify(body),
        method: 'PUT',
      },
    });
  }

  delete<ReturnType>(url: string, body?: unknown) {
    return this.request<ReturnType>({
      url,
      params: {
        body: JSON.stringify(body),
        method: 'DELETE',
      },
    });
  }

  async request<ReturnType>({
    url,
    params = {},
    headers,
    cb,
    options,
  }: {
    url: string;
    params?: ObjectType;
    headers?: ObjectType;
    cb?: (baseHeaders: ObjectType, fullUrl: string) => Promise<ReturnType>;
    options?: Options;
  }): Promise<ReturnType> {
    if (!this.apiUrl) {
      throw new Error('The api url is undefined');
    }

    const token = await this.getToken();
    // Apply predefined headers first, then overwrite with custom headers
    const appSpec = this.providers.getAppSpec();
    const requestHeader = {
      headers: {
        'Accept-Language': this.providers.getLanguage(),
        ...getIzimiMetadataHeaders({ appSpec }),
        ...(this.mockApiKey ? { 'x-api-key': this.mockApiKey } : {}),
        ...(token ? { Authorization: `bearer ${token}` } : {}),
        ...(headers || this.headers),
      },
    };

    if (cb) {
      return cb(requestHeader.headers, this.apiUrl + url);
    }

    if (this.providers.logRequests) {
      console.debug('🛜 api request', { url: this.apiUrl + url, params });
    }

    const response = await fetch(this.apiUrl + url, {
      ...requestHeader,
      ...params,
    });

    return this.handleResponse(response, options);
  }

  private async handleResponse(res: Response, options?: Options) {
    if (!res.ok) {
      return this.providers.handleErrorResponse(res);
    }

    if (options?.getRawSuccessResponse) {
      return res;
    }

    if (res.status === 204) {
      return Promise.resolve({});
    }

    if (options?.resAsBlob) {
      if (options?.withResponseHeaders) {
        return Promise.all([res.blob(), res.headers]);
      }
      return res.blob();
    }

    const text = await res.text();
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      return text;
    }
  }
}

export default FetchService;
